import React from "react";
import { CircleCheck, XCircle, Lightbulb, AlertTriangle } from "lucide-react";
import { AlertType, getAlertStyles } from "./alert-styles";

export interface AlertIconProps {
  className?: string;
}

export const LoadingSpinner: React.FC<AlertIconProps> = ({ className }) => (
  <div
    className={`animate-spin h-4 w-4 border-2 border-orange-400 border-t-transparent rounded-full ${className}`}
  />
);

export const alertIcons = {
  loading: LoadingSpinner,
  success: CircleCheck,
  error: XCircle,
  warning: AlertTriangle,
  info: Lightbulb,
  infoBlue: Lightbulb,
} as const;

export interface AlertConfig {
  type: AlertType;
  title: string;
  message: string;
  icon?: React.ReactNode;
  showAction?: boolean;
  actionLabel?: string;
  onAction?: () => void;
}

export const createAlertIcon = (
  type: AlertType,
  customIcon?: React.ReactNode,
): React.ReactNode => {
  if (customIcon) return customIcon;

  const IconComponent = alertIcons[type];
  const styles = getAlertStyles(type);

  return <IconComponent className={`h-4 w-4 flex-shrink-0 ${styles.icon}`} />;
};

export const getDefaultAlertTitle = (type: AlertType): string => {
  const titles = {
    loading: "Aguardando análise",
    success: "Dentro do padrão",
    error: "Fora do padrão",
    warning: "Atenção necessária",
    info: "Informação",
    infoBlue: "Sugestão de preço",
  };

  return titles[type];
};

export const formatAlertMessage = (
  message: string,
  params?: Record<string, any>,
): string => {
  if (!params) return message;

  return Object.entries(params).reduce(
    (formattedMessage, [key, value]) =>
      formattedMessage.replace(new RegExp(`{{${key}}}`, "g"), String(value)),
    message,
  );
};

export const createAlertConfig = (
  type: AlertType,
  title: string,
  message: string,
  options?: {
    icon?: React.ReactNode;
    showAction?: boolean;
    actionLabel?: string;
    onAction?: () => void;
  },
): AlertConfig => {
  return {
    type,
    title,
    message,
    icon: options?.icon,
    showAction: options?.showAction || false,
    actionLabel: options?.actionLabel,
    onAction: options?.onAction,
  };
};
